import React, { useState } from "react";
import { withRouter, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useQuery, useMutation } from "react-apollo-hooks";
import { toast } from "react-toastify";
import ReactMoment from "react-moment";
import NumberFormat from "react-number-format";
import qs from "qs";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../../../Components/Panel/Panel";
import Pagination from "../../../../Components/Pagination/QSP";
import {
  commonStatusArray,
  commonStatusName,
  pay_methodName,
  currencyName,
  userLabel,
} from "../../../../Components/User/UserNames";
import {
  CHARGEMANAGER_CHARGEREQUEST_LIST_QUERY,
  CHARGEMANAGER_APPROVAL_QUERY,
  CHARGEMANAGER_CANCEL_QUERY,
} from "../../Queries";

// 담당 충전내역 목록. 서버가 담당 범위를 강제하므로 화면에서 범위를 넓힐 수 없다.
export default withRouter(({ history, location, match }) => {
  const chargeRequestConfig = match.params.chargeRequestConfig;
  const baseUrl = `/chargeManager/configs/${chargeRequestConfig}/chargeRequests`;

  const queryString = qs.parse(location.search, { ignoreQueryPrefix: true });
  const page = queryString.page ? parseInt(queryString.page) : 1;
  const commonStatus = queryString.commonStatus || "";
  const keyword = queryString.keyword || "";

  const first = 20;
  const blockSize = 10;
  const skip = (page - 1) * first;

  const [keywordInput, setKeywordInput] = useState(keyword);

  const variables = {
    chargeRequestConfig,
    commonStatus: commonStatus || undefined,
    keyword: keyword || undefined,
    orderBy: "createdAt_DESC",
    skip,
    first,
  };

  const { data, loading, refetch } = useQuery(
    CHARGEMANAGER_CHARGEREQUEST_LIST_QUERY,
    { variables, fetchPolicy: "network-only" }
  );

  const [approvalMutation] = useMutation(CHARGEMANAGER_APPROVAL_QUERY);
  const [cancelMutation] = useMutation(CHARGEMANAGER_CANCEL_QUERY);

  const handleSearch = (e) => {
    e.preventDefault();
    history.push(
      `${baseUrl}?${qs.stringify({ commonStatus, keyword: keywordInput, page: 1 })}`
    );
  };

  const handleStatus = (e) => {
    history.push(
      `${baseUrl}?${qs.stringify({ commonStatus: e.target.value, keyword, page: 1 })}`
    );
  };

  const handleApproval = async (id) => {
    if (!window.confirm("해당 충전요청을 승인하시겠습니까? 승인 시 즉시 충전됩니다."))
      return;
    try {
      const { data: result } = await approvalMutation({ variables: { id } });
      if (result && result.ChargeManagerChargeRequestApproval) {
        toast.success("승인되었습니다.");
        refetch();
      } else {
        toast.error("승인에 실패했습니다.");
      }
    } catch (e) {
      toast.error(String(e).replace("Error: GraphQL error:", "").trim());
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("해당 충전요청을 취소하시겠습니까?")) return;
    try {
      const { data: result } = await cancelMutation({ variables: { id } });
      if (result && result.ChargeManagerChargeRequestCancel) {
        toast.success("취소되었습니다.");
        refetch();
      } else {
        toast.error("취소에 실패했습니다.");
      }
    } catch (e) {
      toast.error(String(e).replace("Error: GraphQL error:", "").trim());
    }
  };

  const list = data && data.ChargeManagerChargeRequestList;
  const rows = (list && list.chargeRequests) || [];
  const count = (list && list.count) || 0;

  return (
    <>
      <Helmet>
        <title>한밭페이 | 충전내역</title>
      </Helmet>
      <h1 className="page-header">
        충전내역{" "}
        <Link to="/chargeManager/configs" className="btn btn-white btn-xs">
          담당 목록으로
        </Link>
      </h1>

      <Panel>
        <PanelHeader noButton={true}>충전내역 ({count}건)</PanelHeader>
        <PanelBody>
          <form onSubmit={handleSearch} className="row m-b-15">
            <div className="col-md-3 m-b-5">
              <select
                className="form-control"
                value={commonStatus}
                onChange={handleStatus}
              >
                <option value="">전체 상태</option>
                {Object.keys(commonStatusArray).map((status) => (
                  <option key={status} value={status}>
                    {commonStatusName(status)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 m-b-5">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="이름 · 연락처 · 이메일 · 가상계좌 검색"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                />
                <span className="input-group-append">
                  <button type="submit" className="btn btn-primary">
                    검색
                  </button>
                </span>
              </div>
            </div>
          </form>

          {loading ? (
            <div className="panel-loader">
              <span className="spinner-small"></span>
            </div>
          ) : count === 0 ? (
            <div className="alert alert-warning m-b-0">
              조회된 충전내역이 없습니다.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped m-b-0">
                  <thead>
                    <tr>
                      <th>상태</th>
                      <th>회원</th>
                      <th>결제수단</th>
                      <th className="text-right">금액</th>
                      <th>가상계좌</th>
                      <th>요청일시</th>
                      <th style={{ width: "150px" }}>처리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td>{commonStatusName(row.commonStatus)}</td>
                        <td>
                          {/* 계정 기준으로 표시한다. 관리자 화면과 같은 이름(아이디) 형식. */}
                          {userLabel(row.user)}
                          <br />
                          <small className="text-muted">{row.buyer_tel}</small>
                          {/* 입금자명이 회원명과 다르면 승인 전에 확인이 필요하므로 따로 보여준다. */}
                          {row.user && row.buyer_name !== row.user.name && (
                            <>
                              <br />
                              <small className="text-warning">
                                입금자 {row.buyer_name}
                              </small>
                            </>
                          )}
                        </td>
                        <td>{pay_methodName(row.pay_method)}</td>
                        <td className="text-right">
                          <NumberFormat
                            value={row.amount}
                            displayType="text"
                            thousandSeparator={true}
                          />
                          원
                          <br />
                          <small className="text-muted">
                            {currencyName(row.currency)}
                          </small>
                        </td>
                        <td>
                          {row.vbank_num ? (
                            <>
                              {row.vbank_name}
                              <br />
                              <small className="text-muted">
                                {row.vbank_num}
                              </small>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <ReactMoment format="YYYY-MM-DD HH:mm">
                            {row.createdAt}
                          </ReactMoment>
                        </td>
                        <td>
                          {row.commonStatus === "S" ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary btn-xs m-r-5"
                                onClick={() => handleApproval(row.id)}
                              >
                                승인
                              </button>
                              <button
                                type="button"
                                className="btn btn-white btn-xs"
                                onClick={() => handleCancel(row.id)}
                              >
                                취소
                              </button>
                            </>
                          ) : (
                            <span className="text-muted">처리완료</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                totalRecord={count}
                blockSize={blockSize}
                pageSize={first}
                currentPage={page}
                baseUrl={baseUrl}
              />
            </>
          )}
        </PanelBody>
      </Panel>
    </>
  );
});
